import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { ActivatedRoute, RouterEvent } from '@angular/router';
import { filter, pluck, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ModalService } from '../../../../core/modal/modal.service';
import { LanguageService } from '../../../../core/services/language.service';
import { ConfirmBySmsComponent } from '../../../../shared/components/modals/confirm-by-sms/confirm-by-sms.component';
import { Address, EditEntrepreneurFields } from '../../organisation.model';
import {
  RegisterEntrepreneurFields,
  RegistrationTypes,
} from '../../../auth/registration/register-organization/register-organization.model';
import { ValidationService } from '../../../../shared/components/form-controls/validators/validation.service';
import { REGEXP_ONLY_DIGITS } from '../../../../core/services/regexp';
import { OrganizationApiService } from '../../../../core/modules/organization/organization-api.service';
import { BankData, OrganisationForm } from '../../../../core/modules/organization/organization.types';

export interface DropdownBankData extends Array<BankData> {
  type?: 'bank';
}
export interface DropdownOrganisationForm extends Array<OrganisationForm> {
  type?: 'orgTypes';
}

@UntilDestroy()
@Component({
  selector: 'tax-edit-individual',
  templateUrl: './edit-individual.component.html',
  styleUrls: ['./edit-individual.component.sass'],
})

export class EditIndividualComponent implements OnInit, OnChanges {
  // @ts-ignore
  @Input() organization: EditEntrepreneurFields | RegisterEntrepreneurFields;
  @Input() isPasswordValid: boolean = true;
  @Input() isRegistration = false;

  @Output() goBack: EventEmitter<void> = new EventEmitter<void>();
  @Output() submitForm: EventEmitter<EditEntrepreneurFields | RegisterEntrepreneurFields> = new EventEmitter<EditEntrepreneurFields | RegisterEntrepreneurFields>();

  readonly registrationTypes = RegistrationTypes;

  fio?: string;
  registrationType?: RegistrationTypes;
  isEmailVerified = false;
  isSameAddress = true;
  organizationTypes: DropdownOrganisationForm = [];
  banks: DropdownBankData = [];

  constructor(
    private organizationApiService: OrganizationApiService,
    private modalService: ModalService,
    private languageService: LanguageService,
    private route: ActivatedRoute,
    private validationService: ValidationService,
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(
        pluck('registrationType'),
        untilDestroyed(this),
      )
      .subscribe((registrationType) => {
        this.registrationType = registrationType
          ? Number(registrationType)
          : RegistrationTypes.EDS;
      });
    this.getOrganizationTypes();
    this.getBanks();
    this.isSameAddress = JSON.stringify(this.organization.actualAddress) === JSON.stringify(this.organization.legalAddress);
  }

  ngOnChanges(): void {
    const user = this.organization?.taxpayer;
    if (user?.lastName && user?.firstName && user?.secondName) {
      this.fio = `${user.lastName} ${user.firstName} ${user.secondName}`;
    }
  }

  private getBanks(): void {
    this.organizationApiService.fetchBanks()
      .pipe(untilDestroyed(this))
      .subscribe((response) => {
        this.banks = response;
        this.banks.type = 'bank';
      });
  }

  private getOrganizationTypes(): void {
    this.languageService.languageChanges
      .pipe(
        switchMap(() => this.organizationApiService.fetchOrganisationTypes()),
        untilDestroyed(this),
      )
      .subscribe((response) => {
        this.organizationTypes = response;
        this.organizationTypes.type = 'orgTypes';
      });
  }

  toggleActualAddress(): void {
    this.isSameAddress = !this.isSameAddress;
    // @ts-ignore
    this.organization.actualAddress = this.isSameAddress
      ? this.organization.legalAddress
      : undefined;
  }

  emailVerifier(email: any): void {
    this.isEmailVerified = email;
    this.organization.email = email;
  }

  setLegalAddress(address: Address): void {
    this.organization.legalAddress = address;
    if (this.isSameAddress) {
      this.organization.actualAddress = this.organization.legalAddress;
    }
  }

  setActualAddress(address: Address): void {
    this.organization.actualAddress = address;
  }

  back(): void {
    this.goBack.emit();
  }

  selectedOrganizationTypes(item: { id: number; name: string }): void {
    this.organization.organisationForm = item.id.toString();
  }

  selectedBank(item: { id: string; name: string }): void {
    this.organization.bank = item.name;
    this.organization.bic = item.id;
  }

  isOrganizationFieldsValid(): boolean {
    return Boolean(this.organization
      && (
        this.registrationType === RegistrationTypes.EDS
        || (this.registrationType === RegistrationTypes.SMS && this.fio && this.organization.organisationForm)
      )
      && this.organization.legalAddress?.fullAddress
      && this.organization.actualAddress?.fullAddress
      && this.organization.email
      && this.isPhoneValid(this.organization.phone)
      && this.isIikValid(this.organization.paymentAccount));
  }

  isFormValid(): boolean {
    if (this.isRegistration) {
      return (
        (this.organization as RegisterEntrepreneurFields).confirmOffer
        && this.isPasswordValid
        && this.isOrganizationFieldsValid()
      );
    }
    return this.isOrganizationFieldsValid();
  }

  iikChange(event: Event | RouterEvent): void {
    if (event instanceof RouterEvent) return;

    this.organization.paymentAccount = (event.target as HTMLInputElement).value;
  }

  isIikValid(iik?: string): boolean {
    return iik ? iik.length === 20 : true;
  }

  isPhoneValid(phone?: string): boolean {
    if (!phone) {
      return false;
    }

    const phoneNormalized = phone.match(REGEXP_ONLY_DIGITS)?.join('');
    return phoneNormalized ? phoneNormalized.length === 11 : false;
  }

  onSubmit(): void {
    if (this.fio) {
      [this.organization.taxpayer.lastName, this.organization.taxpayer.firstName, this.organization.taxpayer.secondName] = this.fio.split(' ');
    }
    this.submitForm.emit(this.organization);
  }

  setPhone(): void {
    this.modalService
      .openOld(ConfirmBySmsComponent, {
        title: this.organization.phone ? 'user.phone.change.modal.title' : 'user.phone.add.modal.title',
        buttonText: this.organization.phone ? 'button.change' : 'button.add',
        businessProcessData: {
          IIN: this.organization.INN,
          userLang: this.languageService.currentLanguage.language,
        },
        phoneValidator: this.validationService.samePhoneValidator(this.organization.phone),
      })
      .pipe(
        filter(phone => Boolean(phone)),
        untilDestroyed(this),
      )
      .subscribe((phone) => {
        this.organization.phone = phone;
      });
  }
}
